import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "../lib/api";

// 3 Nov 2026: public student reviews for a course (only reviews the admin has not hidden). Renders nothing when there are none.
export function Stars({ value, size = 16, color = "#E5A83A", empty = "rgba(160,150,135,.45)" }) {
    return (
        <span className="inline-flex items-center gap-0.5" aria-label={`${value} out of 5`}>
            {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} style={{ width: size, height: size, color: i <= Math.round(value) ? color : empty }} fill={i <= Math.round(value) ? color : "none"} />
            ))}
        </span>
    );
}

export default function CourseReviews({ slug, dark = false, className = "", showEmpty = false }) {
    const [d, setD] = useState(null);
    const [all, setAll] = useState(false);
    useEffect(() => { if (slug) api.get(`/courses/${slug}/reviews`).then((r) => setD(r.data)).catch(() => setD(null)); }, [slug]);
    // 3 Nov 2026 (RK): the star rating on course cards links here (#reviews); scroll once the reviews have loaded
    useEffect(() => {
        if (d && window.location.hash === "#reviews") setTimeout(() => document.getElementById("reviews")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }, [d]);
    if (!d) return null;
    if (!d.count) return showEmpty ? (
        <section id="reviews" className={`${className} text-center scroll-mt-24`} data-testid="course-reviews-empty">
            <h2 className="font-display font-semibold text-2xl sm:text-3xl" style={{ color: dark ? "#F3EFE7" : "#17120C" }}>What students say</h2>
            <p className="mt-2 text-sm" style={{ color: dark ? "#A79F92" : "#6F6656" }}>Reviews from students who finish the course appear here, with their name.</p>
        </section>
    ) : null;
    const shown = all ? d.reviews : d.reviews.slice(0, 6);
    const ink = dark ? "#F3EFE7" : "#17120C", muted = dark ? "#A79F92" : "#6F6656";
    const card = dark ? { background: "#120d08", border: "1px solid rgba(255,255,255,.08)" } : { background: "#fff", border: "1px solid #e8e1d4" };
    const date = (s) => { try { return new Date(s).toLocaleDateString("en-IN", { month: "short", year: "numeric" }); } catch { return ""; } };
    return (
        <section id="reviews" className={`${className} scroll-mt-24`} data-testid="course-reviews">
            <div className="flex flex-col items-center gap-2 text-center">
                <h2 className={dark ? "uppercase text-[30px] sm:text-4xl lg:text-[44px] leading-[0.95]" : "font-display font-semibold text-2xl sm:text-3xl"} style={dark ? { fontFamily: "'Anton', 'Oswald', sans-serif", color: ink } : { color: ink }}>What students say</h2>
                <span className="inline-flex items-center gap-2 text-sm" style={{ color: muted }}><Stars value={d.average} /> <b style={{ color: ink }}>{d.average}</b> from {d.count} review{d.count === 1 ? "" : "s"}</span>
            </div>
            <div className="mt-5 grid sm:grid-cols-2 gap-3 max-w-4xl mx-auto">
                {shown.map((r, i) => (
                    <div key={i} className="rounded-2xl p-5 text-center flex flex-col items-center" style={card}>
                        <Stars value={r.rating} size={15} />
                        <span className="mt-1 text-[11px]" style={{ color: muted }}>{date(r.created_at)}</span>
                        {r.text && <p className="mt-2 text-[14.5px] leading-snug" style={{ color: ink }}>"{r.text}"</p>}
                        <div className="mt-3 flex items-center gap-2">
                            <span className="h-8 w-8 rounded-full grid place-items-center text-xs font-bold" style={{ background: "#E5A83A", color: "#17120C" }}>{(r.name || "S").charAt(0)}</span>
                            <span className="text-left leading-tight"><b className="block text-[13px]" style={{ color: ink }}>{r.name}</b><span className="text-[11px]" style={{ color: muted }}>Verified student · completed the course</span></span>
                        </div>
                    </div>
                ))}
            </div>
            {d.reviews.length > 6 && !all && <div className="mt-4 text-center"><button onClick={() => setAll(true)} className="text-sm underline underline-offset-4" style={{ color: muted }}>Show all reviews</button></div>}
        </section>
    );
}
